import { html, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { BlocksElement } from '../../BlocksElement';
import styles from '../icon.styles';
import type { IconColorType, IconSize } from './icon.model';

/**
 * Health component
 * @csspart base- The component's base wrapper
 */
export class BlocksIconHealth extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Icon color variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li>No color defined renders an icon with the currentColor of its parent element</li>
   * <li>**content-strong** renders an icon with the cat-theme-color-content-strong color token</li>
   * <li>**knockout** renders an icon with the cat-theme-color-content-knockout color token</li>
   * <li>**knockout-brand** renders a renders an icon with the cat-theme-color-content-knockout-brand color token</li>
   * <li>**information** renders an icon with the cat-theme-color-content-utility-information color token</li>
   * <li>**success** renders an icon with the cat-theme-color-content-utility-success color token</li>
   * <li>**warning** renders a renders an icon with the cat-theme-color-content-utility-warning color token</li>
   * <li>**error** renders an icon with the cat-theme-color-content-utility-error color token</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  color: IconColorType;

  /**
   * Title
   * 1) Icons should always have associated text. Use this to supply `aria-label` to icon if it stands by itself without any visual/visually-hidden text
   */
  @property({ type: String })
  iconTitle: string | undefined;

  /**
   * Size property
   * - Default size is 16px
   * - **sm** renders a larger size than default (12px)
   * - **lg** renders a larger size than default (24px)
   * - **xl** renders a larger size than the lg variant (32px);
   */
  @property({ type: String })
  size: IconSize;

  connectedCallback() {
    super.connectedCallback();
    this.classList.add('cat-icon');
  }

  render() {
    const componentClassName = this.componentClassNames('cat-c-icon', {
      'cat-c-icon--sm': this.size === 'sm',
      'cat-c-icon--lg': this.size === 'lg',
      'cat-c-icon--xl': this.size === 'xl',
      'cat-c-icon--content-strong': this.color === 'content-strong',
      'cat-c-icon--knockout': this.color === 'knockout',
      'cat-c-icon--knockout-brand': this.color === 'knockout-brand',
      'cat-c-icon--information': this.color === 'information',
      'cat-c-icon--success': this.color === 'success',
      'cat-c-icon--warning': this.color === 'warning',
      'cat-c-icon--error': this.color === 'error'
    });

    return html`
      <span part="base" class="${componentClassName}" role="presentation">
        <svg
          aria-hidden="${!this.iconTitle}"
          aria-label="${ifDefined(this.iconTitle)}"
          role="${this.iconTitle && 'img'}"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M12.5649 6.67171C14.0757 4.94034 15.5358 4.26232 16.7602 4.17427C17.9841 4.08626 19.0901 4.57831 19.9252 5.41939C21.6291 7.13525 22.0569 10.105 20.0941 12.101L12.0016 19.563L7.46878 15.3529C7.16529 15.071 6.69073 15.0885 6.40885 15.392C6.12696 15.6955 6.14448 16.1701 6.44798 16.452L11.4894 21.1344C11.7764 21.401 12.2202 21.4018 12.5082 21.1362L21.1239 13.1917C21.1323 13.184 21.1404 13.1761 21.1484 13.168C23.8235 10.466 23.1494 6.53747 20.9896 4.36247C19.8914 3.25644 18.3723 2.55447 16.6526 2.67814C15.1221 2.7882 13.526 3.54471 11.9971 5.08118C9.77001 2.86667 7.4071 2.30023 5.38309 2.91922C3.24351 3.57354 1.69105 5.49813 1.24501 7.64959C1.16093 8.05518 1.42156 8.45214 1.82715 8.53622C2.23274 8.62031 2.6297 8.35968 2.71378 7.95409C3.06376 6.26595 4.27394 4.827 5.82176 4.35364C7.31285 3.89763 9.32966 4.28163 11.437 6.6743C11.5797 6.83641 11.7855 6.92909 12.0015 6.92859C12.2175 6.9281 12.4229 6.83448 12.5649 6.67171ZM8.74891 8.50681C8.62362 8.28754 8.39639 8.14605 8.14433 8.13036C7.89228 8.11467 7.64925 8.22688 7.49773 8.42891L5.38152 11.2505H1.85449C1.44028 11.2505 1.10449 11.5863 1.10449 12.0005C1.10449 12.4147 1.44028 12.7505 1.85449 12.7505H5.75652C5.99258 12.7505 6.21488 12.6394 6.35652 12.4505L8.01286 10.2421L10.5682 14.7138C10.6935 14.9331 10.9207 15.0746 11.1727 15.0903C11.4248 15.106 11.6678 14.9938 11.8194 14.7917L13.9356 11.9701H15.9018C16.316 11.9701 16.6518 11.6343 16.6518 11.2201C16.6518 10.8059 16.316 10.4701 15.9018 10.4701H13.5606C13.3245 10.4701 13.1022 10.5813 12.9606 10.7701L11.3042 12.9786L8.74891 8.50681Z"
          />
        </svg>
      </span>
    `;
  }
}

if (customElements.get('cat-icon-health') === undefined) {
  customElements.define('cat-icon-health', BlocksIconHealth);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-icon-health': BlocksIconHealth;
  }
}
